import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import PixelGrid from "@/components/PixelGrid";
import { Grid3X3, MousePointer, Layers, Share2 } from "lucide-react";
import useKonamiCode from "@/hooks/useKonamiCode";
import useSEO from "@/hooks/useSEO";
import useIdlePrefetch from "@/hooks/useIdlePrefetch";
import { useLanguage } from "@/LanguageContext";
import { t } from "@/translations";
import { useSelection } from "@/SelectionContext";
import NoBlocksModal from "@/components/NoBlocksModal";
import { websiteSchema, breadcrumbSchema } from "@/utils/schema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { selectedBlocks, setSelectedBlocks, getBuyUrl } = useSelection();
  const navigate = useNavigate();
  const [selectionMode, setSelectionMode] = useState("single");
  const [vipMode, setVipMode] = useState(false);
  const [heroBackground, setHeroBackground] = useState("");
  const [heroOpacity, setHeroOpacity] = useState(0.15);
  const [showNoBlocks, setShowNoBlocks] = useState(false);
  const { lang } = useLanguage();
  const parallaxRef = useRef(null);

  useSEO({
    title: t("seo.homeTitle", lang),
    description: t("seo.homeDesc", lang),
    canonical: "/",
    jsonLd: websiteSchema(),
  });

  // Prefetch the most-likely-next-routes during browser idle time so the
  // first navigation off the homepage feels instant (saves 200–400 ms on the
  // second route click). BuyPixelsPage is already eager-loaded; these are the
  // next-most-clicked destinations from the homepage.
  useIdlePrefetch([
    () => import("@/pages/BlogPage"),
    () => import("@/pages/MyBlocksPage"),
    () => import("@/pages/FAQPage"),
    () => import("@/pages/GoldenTierPage"),
  ]);

  // Fetch hero background from admin settings
  useEffect(() => {
    const fetchBg = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/site-settings`);
        if (res.ok) {
          const data = await res.json();
          if (data.background_url) {
            const baseUrl = data.background_url.startsWith("http")
              ? data.background_url
              : `${BACKEND_URL}${data.background_url}`;
            // Cache-bust: append updated_at timestamp so browser always fetches the latest image
            const cacheBuster = data.background_updated_at ? `?v=${encodeURIComponent(data.background_updated_at)}` : "";
            const finalUrl = `${baseUrl}${cacheBuster}`;
            setHeroBackground(finalUrl);
            // Perf: inject a high-priority preload hint so the browser starts fetching
            // the LCP image in parallel with JS execution (cuts LCP on slow networks).
            try {
              if (!document.querySelector(`link[rel="preload"][data-hero="1"]`)) {
                const link = document.createElement("link");
                link.rel = "preload";
                link.as = "image";
                link.href = finalUrl;
                link.setAttribute("fetchpriority", "high");
                link.setAttribute("data-hero", "1");
                document.head.appendChild(link);
              }
            } catch {}
          }
          if (typeof data.hero_opacity === "number") {
            setHeroOpacity(data.hero_opacity);
          }
        }
      } catch {}
    };
    fetchBg();
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    if (!heroBackground) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (parallaxRef.current) {
            const scrollY = window.scrollY;
            parallaxRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [heroBackground]);

  const activateVip = useCallback(() => {
    if (vipMode) return;
    setVipMode(true);
    setTimeout(() => setVipMode(false), 5000);
  }, [vipMode]);

  useKonamiCode(activateVip);

  const handleSelectionChange = (blocks) => {
    setSelectedBlocks(blocks);
  };

  const switchMode = (m) => {
    setSelectionMode(m);
    setSelectedBlocks([]);
  };

  return (
    <Layout>
      {/* VIP overlay */}
      {vipMode && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/20 to-[#14B8A6]/20 animate-pulse" />
          <p className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-heading font-black text-6xl text-[#F59E0B] animate-bounce drop-shadow-lg" style={{ textShadow: "0 0 40px #F59E0B" }}>
            VIP MODE
          </p>
        </div>
      )}

      {/* Hero + Features with shared parallax background */}
      <div className="hero-features-wrapper" style={{ position: "relative", overflow: "hidden" }}>
        {/* Parallax background layer */}
        {heroBackground && (
          <div
            ref={parallaxRef}
            className="parallax-bg"
            style={{
              position: "absolute",
              inset: "-20% 0",
              backgroundImage: `url(${heroBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              imageRendering: "auto",
              opacity: heroOpacity,
              zIndex: 0,
              willChange: "transform",
              pointerEvents: "none",
            }}
            data-testid="parallax-background"
          />
        )}

        {/* Hero Section */}
        <section className="hero py-16 md:py-24 relative" style={{ background: heroBackground ? "transparent" : undefined }} data-testid="hero-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] mb-6">
              {t("home.title", lang)}
            </h1>
            <p className="font-pixel text-xl md:text-2xl text-[#0F172A] tracking-wider mb-8">
              {t("home.subtitle", lang)}
            </p>
            <div className="max-w-3xl mx-auto">
              <p className="text-lg md:text-xl text-[#0F172A] leading-relaxed mb-8">
                {t("home.description", lang)}{" "}
                <span className="text-[#0F172A] font-semibold">{t("home.buyPiece", lang)}</span>{" "}
                {t("home.onTheInternet", lang)}{" "}
                <span className="text-[#FF6B6B] font-bold">$100 USD/{t("home.year", lang)}</span>{" "}
                {t("home.priceInfo", lang)}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (selectedBlocks.length > 0) {
                    navigate(getBuyUrl());
                  } else {
                    setShowNoBlocks(true);
                  }
                }}
                className="btn-primary text-lg"
                data-testid="hero-buy-pixels-btn"
              >
                {selectedBlocks.length > 0
                  ? `${t("home.buyNow", lang)} (${selectedBlocks.length} ${t("home.blocksWord", lang)} - $${(selectedBlocks.length * 100).toLocaleString()})`
                  : t("home.buyNow", lang)}
              </button>
              <Link to="/golden-tier" className="btn-secondary text-lg" data-testid="hero-golden-tier-btn">
                {t("home.goldenInfo", lang)}
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section — shares parallax background */}
        <section className="py-16 md:py-24 relative border-t-4 border-[#0F172A]" style={{ background: "transparent" }} data-testid="features-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-[#0F172A] text-center mb-12">
              {t("home.whyBuy", lang)}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="card hover:shadow-brutal-lg transition-all">
                <div className="w-16 h-16 bg-[#14B8A6] flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-2">{t("home.yearlyVisibility", lang)}</h3>
                <p className="text-[#64748B]">{t("home.yearlyVisibilityDesc", lang)}</p>
              </div>

              <div className="card hover:shadow-brutal-lg transition-all">
                <div className="w-16 h-16 bg-[#0EA5E9] flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-2">{t("home.qualityBacklinks", lang)}</h3>
                <p className="text-[#64748B]">{t("home.qualityBacklinksDesc", lang)}</p>
              </div>

              <div className="card hover:shadow-brutal-lg transition-all">
                <div className="w-16 h-16 bg-[#F59E0B] flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-2">{t("home.digitalHistory", lang)}</h3>
                <p className="text-[#64748B]">{t("home.digitalHistoryDesc", lang)}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Grid Section */}
      <section className={`py-12 md:py-20 bg-[#F8FAFC] ${vipMode ? "vip-mode" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#0F172A] mb-2">
              {t("home.theBillboard", lang)}
            </h2>
            <p className="font-pixel text-[#64748B] tracking-wider mb-4">
              {t("home.clickAvailable", lang)}
            </p>

            {/* Selection Mode Toggle */}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              <button
                onClick={() => switchMode("single")}
                className={`flex items-center gap-2 px-4 py-2 border-2 transition-colors ${
                  selectionMode === "single"
                    ? "bg-[#0F172A] text-white border-[#0F172A]"
                    : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0F172A]"
                }`}
                data-testid="single-select-btn"
              >
                <MousePointer size={16} />
                <span className="font-pixel text-sm">{t("home.singleSelect", lang)}</span>
              </button>
              <button
                onClick={() => switchMode("multi")}
                className={`flex items-center gap-2 px-4 py-2 border-2 transition-colors ${
                  selectionMode === "multi"
                    ? "bg-[#14B8A6] text-white border-[#14B8A6]"
                    : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#14B8A6]"
                }`}
                data-testid="multi-select-btn"
              >
                <Grid3X3 size={16} />
                <span className="font-pixel text-sm">{t("home.multiSelect", lang)}</span>
              </button>
              <button
                onClick={() => switchMode("cluster")}
                className={`flex items-center gap-2 px-4 py-2 border-2 transition-colors ${
                  selectionMode === "cluster"
                    ? "bg-[#F59E0B] text-white border-[#F59E0B]"
                    : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#F59E0B]"
                }`}
                data-testid="cluster-select-btn"
              >
                <Layers size={16} />
                <span className="font-pixel text-sm">{t("home.cluster", lang)}</span>
              </button>
            </div>
          </div>

          <PixelGrid
            onSelectionChange={handleSelectionChange}
            selectedBlocks={selectedBlocks}
            mode={selectionMode}
          />

          {/* Social Sharing */}
          <div className="mt-8 flex items-center justify-center gap-3" data-testid="social-sharing">
            <span className="text-[#64748B] text-sm font-pixel flex items-center gap-1"><Share2 size={14} /> {t("home.share", lang)}</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center bg-[#1877F2] text-white hover:opacity-80 transition-opacity"
              data-testid="share-facebook"
              title={t("home.shareOnFacebook", lang)}
            >
              <i className="fab fa-facebook-f text-sm"></i>
            </a>
            <a
              href={`https://www.instagram.com/VallartaPixelOfficial=${encodeURIComponent(t("home.shareText", lang))}&url=${encodeURIComponent(window.location.origin)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center bg-[#E1306C] text-white hover:opacity-80 transition-opacity"
              data-testid="share-instagram"
              title={t("home.shareOnInstagram", lang)}
            >
              <i className="fab fa-instagram text-sm"></i>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(t("home.shareText", lang))}&url=${encodeURIComponent(window.location.origin)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center bg-[#0F172A] text-white hover:opacity-80 transition-opacity"
              data-testid="share-twitter"
              title={t("home.shareOnX", lang)}
            >
              <i className="fab fa-x-twitter text-sm"></i>
            </a>
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(t("home.shareText", lang) + " " + window.location.origin)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center bg-[#25D366] text-white hover:opacity-80 transition-opacity"
              data-testid="share-whatsapp"
              title={t("home.shareOnWhatsApp", lang)}
            >
              <i className="fab fa-whatsapp text-sm"></i>
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.origin)}&title=${encodeURIComponent("Vallarta Pixel - The PV Hub")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center bg-[#0A66C2] text-white hover:opacity-80 transition-opacity"
              data-testid="share-linkedin"
              title={t("home.shareOnLinkedIn", lang)}
            >
              <i className="fab fa-linkedin-in text-sm"></i>
            </a>
          </div>
        </div>
      </section>

      <NoBlocksModal isOpen={showNoBlocks} onClose={() => setShowNoBlocks(false)} />
    </Layout>
  );
};

export default HomePage;
